import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import AdminSidebar from "./components/AdminSidebar/AdminSidebar";
import LearnersCreating from "../../../components/Insights/StoriesSnapShot/LearnersCreating";
import HowTheyLearning from "../../../components/Insights/StoriesSnapShot/HowTheyLearning";
import HowTheyDoing from "../../../components/Insights/StoriesSnapShot/HowTheyDoing";
import SchoolWork from "../../../components/Insights/SchoolWork/SchoolWork";
import Header from "../../../components/Layout/Header";
import AdminSubHeader from "../../../components/AdminSubHeader/AdminSubHeader";
import AdminStudentView from "./components/StudentView/AdminStudentView";
import { useCurrentUser } from "../../../hooks/useGetUserDetail";
import { useSchoolHierarchy } from "../../../hooks/useSchoolHierarchy";
import { useDashboardFeed } from "../../../hooks/useSchoolFeed";
import { useMetrics } from "../../../hooks/useMetrics";
import GroupView from "./components/GroupView/GroupView";
import LearningPlan from "./components/LearningPlan/LearningPlan";
import useSelScore from "../../../hooks/useSELScore";
import { useTeacherMetrics } from "../../../hooks/useTeacherMetrics";
import { useUpdateUserPreferences } from "../../../hooks/useUpdateUserPreferences";
import { trackEvent } from "../../../mixpanel";
import { PROFILE_TYPE } from "../../../utils/constants";
import Resources from "../../Resources/Resources";
import Activities from "../../Activities/Activities";
import TrooStories from "../../TrooStories/TrooStories";

const Admin = () => {
  const { tab: tabSlug } = useParams<{ tab?: string }>();
  const tab = tabSlug
    ? tabSlug.toUpperCase().replace(/-/g, "_")
    : "CLASSROOM_VIEW";
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [timeRange, setTimeRange] = useState<"Weekly" | "Monthly">("Weekly");
  const [selectedGrade, setSelectedGrade] = useState<string>("");
  const [selectedStudent, setSelectedStudent] = useState<any>(null);
  const [search, setSearch] = useState("");
  const { data: userInfo } = useCurrentUser();

  const { updateGrade } = useUpdateUserPreferences();

  const isPrincipal =
    userInfo?.profileType.toLowerCase() === PROFILE_TYPE.SUPER_ADMIN;
  const isTeacher = userInfo?.profileType.toLowerCase() === PROFILE_TYPE.ADMIN;

  // Tracked tab change handler — navigation is handled by AdminSidebar via URL
  const handleTabChange = (newTab: string) => {
    const userRole = isPrincipal ? "Principal" : isTeacher ? "Teacher" : "";
    trackEvent(`TAB_CLICKED_${userRole.toUpperCase()}`, {
      tab: newTab,
      previousTab: tab,
    });
  };

  // Tracked grade change handler
  const handleGradeChange = (newGrade: string) => {
    const userRole = isPrincipal ? "Principal" : isTeacher ? "Teacher" : "";
    trackEvent(`CLASSROOM_SELECTED_${userRole.toUpperCase()}`, {
      grade: newGrade,
    });
    setSelectedGrade(newGrade);
    updateGrade(newGrade);
  };

  // Tracked student change handler
  const handleStudentChange = (newStudent: any) => {
    const userRole = isPrincipal
      ? "Principal"
      : isTeacher
        ? "Teacher"
        : "Admin";
    trackEvent(`STUDENT_SELECTED_${userRole.toUpperCase()}`, {
      studentId: newStudent?.id,
      grade: selectedGrade,
    });
    setSelectedStudent(newStudent);
  };

  // Tracked time range change handler
  const handleTimeRangeChange = (newTimeRange: "Weekly" | "Monthly") => {
    const userRole = isPrincipal ? "Principal" : isTeacher ? "Teacher" : "";
    const days = newTimeRange === "Weekly" ? "7 days" : "30 days";
    trackEvent(`FILTER_CLICKED_${userRole.toUpperCase()}`, {
      filter: days,
      timeRange: newTimeRange,
      tab,
    });
    setTimeRange(newTimeRange);
  };

  const schoolHash = useMemo(() => {
    return userInfo?.schoolHash ?? null;
  }, [userInfo?.email]);

  const { data: schoolData } = useSchoolHierarchy({
    schoolHash,
    enabled: !!schoolHash,
  });

  const grades = schoolData?.hierarchy || [];

  useEffect(() => {
    if (grades.length > 0 && userInfo && !selectedGrade) {
      // Prefer the grade saved in the user's profile (API returns lowercase e.g. "1a")
      const savedGrade = userInfo?.grade as string | undefined;
      const matched = savedGrade
        ? grades.find(
            (g: any) => g.classId.toLowerCase() === savedGrade.toLowerCase(),
          )
        : null;
      // Set directly — no need to call updateUserPreferences on initial restore
      setSelectedGrade(matched?.classId ?? grades[0].classId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grades, userInfo]);
  // find selected class object
  const selectedClassObj = grades.find((g: any) => g.classId === selectedGrade);

  // students of selected grade — memoised so the array reference is stable
  const students = useMemo(
    () => selectedClassObj?.students || [],
    [selectedClassObj],
  );

  const selectedStudentId = useMemo(() => {
    if (selectedStudent?.id) return selectedStudent.id;
    if (tab === "STUDENT_VIEW" && students.length > 0) return students[0].id;
    return null;
  }, [tab, selectedStudent, students]);

  useEffect(() => {
    if (tab === "STUDENT_VIEW" && !selectedStudent && students.length > 0) {
      setSelectedStudent(students[0]);
    }
    if (tab !== "STUDENT_VIEW") {
      // optional: keep selectedStudent for later, or clear if desired
      // setSelectedStudent(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, students]);

  const { data: ClassSel, loading, error, fetchClassSel } = useSelScore();

  useEffect(() => {
    if (schoolHash && selectedGrade && tab === "CLASSROOM_VIEW") {
      fetchClassSel(schoolHash, selectedGrade);
    }
  }, [schoolHash, selectedGrade, tab]);

  const { data: studentSel, fetchStudentSel } = useSelScore();

  useEffect(() => {
    if (
      schoolHash &&
      selectedClassObj &&
      selectedStudentId &&
      tab === "STUDENT_VIEW"
    )
      fetchStudentSel(schoolHash, selectedGrade, selectedStudentId);
  }, [selectedStudentId, schoolHash, selectedGrade, tab]);

  const {
    data: feedData,
    loading: feedLoading,
    fetchMoreFeed,
    refetch: dashboardFetch,
  } = useDashboardFeed({
    feedScopeType: tab == "STUDENT_VIEW" ? "user" : "grade",
    schoolHash: schoolHash,
    grade: selectedGrade,
    studentId: selectedStudentId,
    enabled: !!schoolHash,
  });

  const {
    data: metricsData,
    loading: mLoading,
    refetch: matricsfetch,
  } = useMetrics({
    scope: tab == "STUDENT_VIEW" ? "student" : "class",
    schoolHash: schoolHash,
    grade: selectedGrade,
    range: timeRange === "Monthly" ? "monthly" : "weekly",
    studentId: tab == "STUDENT_VIEW" ? selectedStudentId : undefined,
    enabled: !!schoolHash,
  });

  const {
    data: teacherMetricsData,
    loading: teacherMLoading,
    refetch: teacherMetricsFetch,
  } = useTeacherMetrics({
    schoolHash: schoolHash ?? undefined,
    grade: selectedGrade,
    username: userInfo?.userName,
    range: timeRange === "Monthly" ? "monthly" : "weekly",
    enabled: !!schoolHash && tab === "TEACHER_VIEW",
  });

  useEffect(() => {
    if (schoolHash && tab === "TEACHER_VIEW") {
      teacherMetricsFetch();
    }
  }, [schoolHash, selectedGrade, timeRange, tab]);

  useEffect(() => {
    setSelectedStudent(null); // reset student when grade changes
  }, [selectedGrade]);

  useEffect(() => {
    if (schoolHash) {
      dashboardFetch();
      matricsfetch();
    }
  }, [schoolHash, selectedGrade, selectedStudent, timeRange, tab]);

  return (
    <div className="admin-root">
      <AdminSidebar
        activeTab={tab}
        onChange={handleTabChange}
        isCollapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed((c) => !c)}
      />
      <div className="admin-right">
        <Header
          isAdmin
          grade={selectedGrade}
          grades={grades}
          onGradeChange={handleGradeChange}
        />
        <AdminSubHeader
          tab={tab}
          selectedStudent={selectedStudent}
          onStudentChange={setSelectedStudent}
          timeRange={timeRange}
          onTimeChange={setTimeRange}
          search={search}
          onSearch={setSearch}
          students={students}
          grades={grades}
          grade={selectedGrade}
          onGradeChange={handleGradeChange}
        />
        <main className="dashboard">
          {tab === "CLASSROOM_VIEW" && (
            <>
              <LearnersCreating
                classroomData={metricsData}
                loading={mLoading}
              />
              <HowTheyDoing
                Admin
                isClassroom={true}
                data={metricsData}
                loading={mLoading}
              />
              {/* <HowTheyLearning
                isSchool={true}
                learningData={metricsData}
                loading={mLoading}
                selData={ClassSel}
                Admin
              /> */}
              <SchoolWork
                title="Your Class's Work"
                loading={feedLoading}
                feedData={feedData}
                fetchMoreFeed={fetchMoreFeed}
                showGrade
              />
            </>
          )}
          {tab === "TEACHER_VIEW" && (
            <>
              <LearnersCreating
                classroomData={teacherMetricsData}
                loading={teacherMLoading}
              />
              <HowTheyDoing
                Admin
                isClassroom={false}
                data={teacherMetricsData}
                loading={teacherMLoading}
              />
              <SchoolWork
                title="Your Class's Work"
                loading={feedLoading}
                feedData={feedData}
                fetchMoreFeed={fetchMoreFeed}
                showGrade
              />
            </>
          )}
          {tab === "STUDENT_VIEW" && (
            <>
              <AdminStudentView
                data={metricsData}
                loading={mLoading}
                name={selectedStudent?.name?.toUpperCase() || ""}
                selData={studentSel}
                classId={selectedGrade}
                schoolHash={schoolHash || ""}
                userId={selectedStudentId || ""}
                isStudent={true}
              />
              <SchoolWork
                loading={feedLoading}
                feedData={feedData}
                fetchMoreFeed={fetchMoreFeed}
                title={selectedStudent?.name?.toUpperCase() || ""}
              />
            </>
          )}
          {tab === "GROUP_VIEW" && (
            <GroupView
              schoolHash={schoolData?.schoolHash || ""}
              grade={selectedGrade}
            />
          )}
          {tab === "LEARNING_PLAN" && (
            <LearningPlan
              schoolHash={schoolData?.schoolHash || ""}
              grade={selectedGrade}
              classroomData={metricsData}
            />
          )}
          {tab === "RESOURCES" && <Resources />}
          {tab === "ACTIVITIES" && <Activities />}
          {tab === "TROO_STORIES" && (
            <TrooStories
              schoolHash={schoolData?.schoolHash || ""}
              teacherId={userInfo?.userName || ""}
              grade={selectedGrade}
              grades={grades}
              onGradeChange={handleGradeChange}
            />
          )}
        </main>
      </div>
    </div>
  );
};

export default Admin;
